/* hcs-ported-tables.js — table + accordion enhancer for the "Ported content"
   section (sections/hcs-ported-content.liquid).

   Audit COL-20 / SITE-31 follow-up: the section used to emit TWO inline
   `<script type="module">` blocks per instance. On /collections/hatch-green-chile
   that is nine copies of the same ~4 KB of JS inside the HTML document, none of
   it cacheable. This file is that same logic, once, as one deferred cacheable
   asset. The section prints a single `<script src=… defer>` when it has tables
   or is collapsible.

   Contract with the Liquid:
     [data-hcs-ported-tables]  root wrapper whose .hcs-ported-content holds tables
     [data-hcs-ported-acc]     root wrapper rendered in collapsible (accordion) mode
     [data-hcs-stack-tables]   on the same wrapper: one-column card mode below 750px

   Idempotent by construction: a duplicate <script> tag (two instances on one
   page, or a re-render) runs this file twice, so the window guard makes the
   second execution a no-op, and every enhance path marks its root so a re-scan
   only ever touches roots that are still raw. `shopify:section:load` replaces
   the section element wholesale, so the root is re-resolved from the DOM by
   re-scanning rather than by holding a stale reference. */
(function () {
  'use strict';

  if (window.__hcsPortedTables) return;
  window.__hcsPortedTables = true;

  var boxes = [];
  var accRoots = [];

  /* In collapsible mode a table can start inside a closed <details>, where it
     measures 0 wide; observe the box so the affordance appears when it opens
     (also covers rotation and late font loads). */
  var ro =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) sync(entries[i].target);
        })
      : null;

  function tableName(table) {
    var caption = table.querySelector('caption');
    if (caption && caption.textContent.trim()) return caption.textContent.trim();
    var prev = table.previousElementSibling;
    while (prev) {
      if (/^H[1-6]$/.test(prev.tagName) && prev.textContent.trim()) {
        return prev.textContent.trim();
      }
      prev = prev.previousElementSibling;
    }
    return 'Comparison table';
  }

  /* Per-cell data-label from the column headers, so the optional one-column
     stack can name each value without JS at render time. */
  function labelCells(table) {
    var headRows = table.querySelectorAll('thead tr');
    if (!headRows.length) return;
    var heads = headRows[headRows.length - 1].children;
    var labels = [];
    for (var h = 0; h < heads.length; h++) {
      labels.push((heads[h].textContent || '').replace(/\s+/g, ' ').trim());
    }
    if (!labels.length) return;
    var rows = table.querySelectorAll('tbody tr');
    for (var r = 0; r < rows.length; r++) {
      var cells = rows[r].children;
      for (var c = 0; c < cells.length && c < labels.length; c++) {
        if (labels[c] && !cells[c].hasAttribute('data-label')) {
          cells[c].setAttribute('data-label', labels[c]);
        }
      }
    }
  }

  /* Stack mode only. Legacy pasted markup carries per-cell inline
     `padding`/`border` from whatever editor produced it; in card mode those
     win over the stylesheet (inline beats a class) and the cards come out with
     ragged double borders and lopsided padding. Removing the declarations at
     wrap time hands the cell back to the section's own rules — no !important
     war, and nothing else on the cell (colors, alignment, width) is touched. */
  function stripCellBoxStyles(table) {
    var cells = table.querySelectorAll('td, th');
    for (var i = 0; i < cells.length; i++) {
      var cell = cells[i];
      var s = cell.style;
      if (!s || !s.length) continue;
      /* Backwards: removeProperty() re-indexes the declaration list. */
      for (var k = s.length - 1; k >= 0; k--) {
        var prop = s[k];
        if (prop.indexOf('padding') === 0 || prop.indexOf('border') === 0) {
          s.removeProperty(prop);
        }
      }
      if (!cell.getAttribute('style')) cell.removeAttribute('style');
    }
  }

  function sync(box) {
    var wrap = box.parentNode;
    if (!wrap) return;
    var overflows = box.scrollWidth - box.clientWidth > 4;
    wrap.setAttribute('data-overflow', overflows ? '1' : '0');
    /* Only a table that actually scrolls needs to be a focusable region;
       announcing a landmark per table would be noise otherwise. */
    if (overflows) {
      box.setAttribute('tabindex', '0');
      box.setAttribute('role', 'region');
    } else {
      box.removeAttribute('tabindex');
      box.removeAttribute('role');
    }
    var atEnd = !overflows || box.scrollLeft + box.clientWidth >= box.scrollWidth - 4;
    box.classList.toggle('is-end', atEnd);
  }

  function enhanceTables(root) {
    var stack = root.hasAttribute('data-hcs-stack-tables');
    var tables = root.querySelectorAll('.hcs-ported-content table');
    for (var i = 0; i < tables.length; i++) {
      var table = tables[i];
      if (table.closest('.hcs-ptbl')) continue;

      labelCells(table);
      if (stack) stripCellBoxStyles(table);

      var wrap = document.createElement('div');
      wrap.className = 'hcs-ptbl-wrap' + (stack ? ' hcs-ptbl-wrap--stack' : '');

      var hint = document.createElement('p');
      hint.className = 'hcs-ptbl__hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.textContent = 'Swipe to compare';

      var box = document.createElement('div');
      box.className = 'hcs-ptbl';
      box.setAttribute('aria-label', tableName(table));

      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(hint);
      wrap.appendChild(box);
      box.appendChild(table);

      box.addEventListener(
        'scroll',
        (function (b) {
          return function () {
            sync(b);
          };
        })(box),
        { passive: true }
      );
      boxes.push(box);
      if (ro) ro.observe(box);
      sync(box);
    }
  }

  /* A closed <details> does not open for a #hash target, so the guide navs'
     jump links would otherwise land on a collapsed heading. */
  function revealHash() {
    var id = location.hash && decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    var target = document.getElementById(id);
    if (!target) return;
    for (var i = 0; i < accRoots.length; i++) {
      var root = accRoots[i];
      if (!root.isConnected || !root.contains(target)) continue;
      var d = target.closest('details');
      if (d && !d.open) {
        d.open = true;
        target.scrollIntoView({ block: 'start' });
      }
      return;
    }
  }

  function scan() {
    /* Drop boxes whose section the editor replaced. */
    boxes = boxes.filter(function (b) {
      return b.isConnected;
    });
    accRoots = accRoots.filter(function (r) {
      return r.isConnected;
    });

    var roots = document.querySelectorAll('[data-hcs-ported-tables], [data-hcs-ported-acc]');
    var addedAcc = false;
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.__hcsPortedDone) continue;
      root.__hcsPortedDone = true;
      if (root.hasAttribute('data-hcs-ported-acc')) {
        accRoots.push(root);
        addedAcc = true;
      }
      if (root.hasAttribute('data-hcs-ported-tables')) enhanceTables(root);
    }
    if (addedAcc) revealHash();
  }

  var pending = false;
  window.addEventListener(
    'resize',
    function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        for (var i = 0; i < boxes.length; i++) sync(boxes[i]);
      });
    },
    { passive: true }
  );

  window.addEventListener('hashchange', revealHash);

  /* The editor replaced a section element; re-resolve every root from the DOM. */
  document.addEventListener('shopify:section:load', scan);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
